'use client'

import { DialogPortal } from '@radix-ui/react-dialog'
import { VisuallyHidden } from '@radix-ui/react-visually-hidden'
import { VariantProps } from 'cva'
import { X } from 'lucide-react'
import React, { ComponentProps, HTMLAttributes } from 'react'
import { Drawer as DrawerPrimitive } from 'vaul'

import { cn } from '../../lib'
import { Box, VStack } from '../layout'

import {
  bottomSheetDoNotShowTodayContainerStyle,
  bottomSheetDoNotShowTodayLabelStyle,
  bottomSheetDoNotShowTodayTextStyle,
  bottomSheetDragHandleStyle,
  bottomSheetOverlayStyle,
  bottomSheetStyle,
} from './bottom-sheet.css'

// z-index 레벨 관리를 위한 컨텍스트
const BottomSheetLevelContext = React.createContext<number>(0)
const useBottomSheetLevel = () => React.useContext(BottomSheetLevelContext)

export const BottomSheetRoot = ({
  repositionInputs = false,
  shouldScaleBackground = true,
  ...props
}: ComponentProps<typeof DrawerPrimitive.Root>) => (
  <BottomSheetLevelContext.Provider value={0}>
    <DrawerPrimitive.Root
      repositionInputs={repositionInputs}
      shouldScaleBackground={shouldScaleBackground}
      {...props}
    />
  </BottomSheetLevelContext.Provider>
)

export const BottomSheetDragHandle = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => {
  return <Box className={cn(bottomSheetDragHandleStyle(), className)} {...props} />
}

export const BottomSheetNestedRoot = ({
  repositionInputs = false,
  shouldScaleBackground = true,
  ...props
}: ComponentProps<typeof DrawerPrimitive.NestedRoot>) => {
  const parentLevel = useBottomSheetLevel()
  const currentLevel = parentLevel + 1
  return (
    <BottomSheetLevelContext.Provider value={currentLevel}>
      <DrawerPrimitive.NestedRoot
        repositionInputs={repositionInputs}
        shouldScaleBackground={shouldScaleBackground}
        {...props}
      />
    </BottomSheetLevelContext.Provider>
  )
}

export const BottomSheetTrigger = DrawerPrimitive.Trigger

export const BottomSheetOverlay = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, style, ...props }, ref) => (
  <DrawerPrimitive.Overlay ref={ref} className={cn(bottomSheetOverlayStyle(), className)} style={style} {...props} />
))

export const BottomSheetPortal = (
  props: Omit<ComponentProps<typeof DialogPortal>, 'children'> & { children: React.ReactNode },
) => <DialogPortal {...props}>{props.children}</DialogPortal>

type BottomSheetContentProps = { closeForTodayTestId?: string; onClickOverlay?: () => void } & (
  | { isVisibleForToday?: false; handleCloseForToday?: undefined }
  | { isVisibleForToday: true; handleCloseForToday: () => void }
)

export const BottomSheetContent = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> &
    BottomSheetContentProps &
    VariantProps<typeof bottomSheetStyle>
>(
  (
    { className, children, size = 'full', isVisibleForToday, handleCloseForToday, closeForTodayTestId, ...props },
    ref,
  ) => {
    const level = useBottomSheetLevel()
    const baseZIndex = 50
    const overlayZIndex = baseZIndex + level * 2
    const contentZIndex = overlayZIndex + 1

    return (
      <BottomSheetPortal>
        <BottomSheetOverlay style={{ zIndex: overlayZIndex }} />
        <VisuallyHidden asChild>
          <DrawerPrimitive.Title />
        </VisuallyHidden>
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(bottomSheetStyle({ size }))}
          style={{ zIndex: contentZIndex, overflow: 'visible', position: 'fixed' }}
          {...props}
        >
          {isVisibleForToday && (
            <DrawerPrimitive.Close asChild>
              <div className={cn(bottomSheetDoNotShowTodayContainerStyle())} style={{ zIndex: contentZIndex + 1 }}>
                <button
                  type="button"
                  className={cn(bottomSheetDoNotShowTodayLabelStyle())}
                  data-testid={closeForTodayTestId}
                  onClick={handleCloseForToday}
                >
                  <span className={cn(bottomSheetDoNotShowTodayTextStyle())}>
                    오늘 하루 다시 보지 않기
                    <X size={14} />
                  </span>
                </button>
              </div>
            </DrawerPrimitive.Close>
          )}
          <BottomSheetDragHandle />
          <Box className={cn('flex-1 overflow-y-auto pb-5', className)}>{children}</Box>
        </DrawerPrimitive.Content>
      </BottomSheetPortal>
    )
  },
)

export const BottomSheetHeader = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <VStack className={cn('gap-4 px-5', className)} {...props}>
    {children}
  </VStack>
)

export const BottomSheetFooter = ({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <VStack className={cn('gap-5 px-5 py-2 mb-[var(--safe-area-inset-bottom)]', className)} {...props}>
    {children}
  </VStack>
)

export const BottomSheetHandle = DrawerPrimitive.Handle

export const BottomSheetTitle = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold leading-none tracking-tight', className)}
    {...props}
  />
))

export const BottomSheetDescription = React.forwardRef<
  React.ComponentRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))

export const BottomSheetClose = DrawerPrimitive.Close
